import { useCallback, useEffect, useRef } from 'react';

import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';

import { type ErrorData } from '@/app/types/yandex';

import { useUser } from '@/entities/user/model/user.store';

import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';
import { useTheme } from '@/shared/store/app-store/app-store';
import { ThemeMode } from '@/shared/store/app-store/types';

import {
  resolveYandexAdsInstance,
  type YaInstance,
} from '../helpers/resolve-yandex-ads-instance';
import { CustomErrorCode } from './types';

const touchBlockId = import.meta.env.VITE_YANDEX_ADS_TOUCH_BLOCK_ID;
const desktopBlockId = import.meta.env.VITE_YANDEX_ADS_DESKTOP_BLOCK_ID;

const SAFE_AREA_STYLE_ID = 'yandex-ads-safe-area-fix';

const SAFE_AREA_VARS = [
  ['--safe-area-inset-top', 'var(--safe-area-top-offset)'],
  ['--safe-area-inset-bottom', 'var(--safe-area-bottom-offset)'],
  ['--safe-area-inset-left', 'var(--safe-area-left-offset)'],
  ['--safe-area-inset-right', 'var(--safe-area-right-offset)'],
] as const;

// yandex defines --safe-area-inset-* via env(), which is 0 inside the telegram
// webview and knows nothing about telegram's native controls. it may be defined
// at any depth of the fullscreen overlay, so a value set on the parent element
// gets shadowed — override the whole subtree with !important instead
const injectSafeAreaStyles = () => {
  if (document.getElementById(SAFE_AREA_STYLE_ID)) return;

  const declarations = SAFE_AREA_VARS.map(
    ([name, value]) => `${name}: ${value} !important;`
  ).join('');

  const style = document.createElement('style');

  style.id = SAFE_AREA_STYLE_ID;
  style.textContent = `
    div[data-header],
    div[data-header] * { ${declarations} }
    :has(> div[data-header]) { ${declarations} }
  `;

  document.head.append(style);
};

interface YandexAdsProps {
  onReward?: () => void;
  onError?: (error: ErrorData) => void;

  onSkip?: () => void;
}

const makeErrorData = (message: string, code = 'CUSTOM_ERROR'): ErrorData => {
  return {
    type: 'error',
    code,
    text: message,
  };
};

export const useYandexAds = ({ onReward, onError, onSkip }: YandexAdsProps = {}) => {
  const { t } = useTranslation();

  const theme = useTheme();

  const user = useUser();

  const yandexInstanceRef = useRef<YaInstance | undefined>(
    window.Ya && window.yaContextCb
      ? {
          yandex: window.Ya,
          cb: window.yaContextCb,
        }
      : undefined
  );

  const getInstances = useCallback(async () => {
    const { yandex, cb } = yandexInstanceRef.current ?? {};

    if (!yandex || !cb) {
      const instance = await resolveYandexAdsInstance();

      if (!instance?.yandex || !instance.cb) return undefined;

      yandexInstanceRef.current = { ...instance };

      return instance;
    }

    return yandexInstanceRef.current;
  }, []);

  const backButtonWasVisibleRef = useRef(WebApp.BackButton.isVisible);

  useEffect(() => {
    void getInstances();

    // fix yandex ads styles for fullscreen
    injectSafeAreaStyles();

    // the stylesheet above loses only to yandex's own inline !important,
    // so duplicate the overrides inline on the header and its parent
    const observer = new MutationObserver((mutations) => {
      // style mutations fire on every frame of inline-style animations
      // (react-spring etc.), so only react to them inside the ad overlay
      const isRelevant = mutations.some((mutation) => {
        if (mutation.type === 'childList') return true;

        const target = mutation.target as Element;

        return (
          !!target.closest?.('div[data-header]') ||
          !!target.querySelector?.(':scope > div[data-header]')
        );
      });

      if (!isRelevant) return;

      const headerDiv = document.querySelector('div[data-header]') as HTMLDivElement;

      if (!headerDiv) return;

      const platform = window.Ya?.Context?.AdvManager.getPlatform();

      if (platform === 'desktop') {
        const headerChild = headerDiv.firstChild as HTMLDivElement;

        if (headerChild) {
          headerChild.style.setProperty(
            'min-width',
            'min(100%, calc(400px + 20%))',
            'important'
          );
        }
      }

      [headerDiv, headerDiv.parentElement].forEach((el) => {
        if (!el) return;

        SAFE_AREA_VARS.forEach(([name, value]) => {
          if (el.style.getPropertyValue(name) !== value) {
            el.style.setProperty(name, value, 'important');
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      // reapply if yandex rewrites the style attribute after render
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = useCallback(
    (err: ErrorData) => {
      console.error(err.text, err.code);

      onError?.(err);
    },
    [onError]
  );

  const { isKassir } = useFeatureFlag();

  const show = useCallback(async () => {
    const handleStart = () => {
      // store prev isVisible of BackButton to display it correctly after ads
      backButtonWasVisibleRef.current = WebApp.BackButton.isVisible;

      if (backButtonWasVisibleRef.current) WebApp.BackButton.hide();
    };

    const handleEnd = () => {
      if (backButtonWasVisibleRef.current) {
        WebApp.BackButton.show();
        backButtonWasVisibleRef.current = true;
      }
    };

    const yandexInstance = await getInstances();

    if (!yandexInstance) {
      return handleError(makeErrorData('Advertisement script not loaded'));
    }

    if (!user?.telegramId) {
      return handleError(makeErrorData('Telegram ID must be passed'));
    }

    const { yandex, cb } = yandexInstance;

    cb.push(() => {
      const platform = yandex.Context.AdvManager.getPlatform();

      yandex.Context.AdvManager.render(
        {
          ...(platform === 'desktop'
            ? {
                blockId: desktopBlockId,
                platform: 'desktop',
                renderTo: `yandex_rtb_${desktopBlockId}`,
              }
            : {
                blockId: touchBlockId,
                platform: 'touch',
                renderTo: `yandex_rtb_${touchBlockId}`,
              }),
          darkTheme: theme === ThemeMode.DARK,
          tgUid: user.telegramId,
          type: 'rewarded',
          onLoad: (data) => {
            if (isKassir) console.log('ON_LOAD', data);
          },
          onRewarded: (isRewarded: boolean) => {
            handleEnd();

            if (isRewarded) onReward?.();
            else onSkip?.();
          },
          onClose: () => {
            handleEnd();
          },
          onError: (err) => {
            if (isKassir) console.log('ON_ERROR', err);

            handleError(err);
          },
          onRender: (data) => {
            if (isKassir) console.log('ON_RENDER', data);
            handleStart();
          },
        },
        () => {
          if (isKassir) console.log('YANDEX_FALLBACK');

          handleError(
            makeErrorData(
              t('Oops, no ads available right now'),
              CustomErrorCode.NO_AVAILABLE_ADS
            )
          );
        }
      );
    });
  }, [getInstances, handleError, isKassir, onReward, onSkip, t, theme, user?.telegramId]);

  return show;
};
